import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { clsx } from 'clsx';
import { createPortal } from 'react-dom';
import CrossIcon from './icons/cross.svg?react';
import { IconButton } from './primitives/IconButton';

const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  className = '',
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overlay = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-spacing-4"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-page opacity-80 backdrop-blur-sm transition-opacity duration-200"
        aria-hidden="true"
      />
      
      <div
        className={clsx(
          'relative w-full',
          'bg-bg-overlay border border-border-default rounded-lg',
          'shadow-elevation-3',
          'p-spacing-6',
          'transition-all duration-200',
          sizeStyles[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {showCloseButton && (
          <div className="absolute top-spacing-4 right-spacing-4">
            <IconButton
              variant="ghost"
              onClick={onClose}
              aria-label="Close modal"
            >
              <CrossIcon />
            </IconButton>
          </div>
        )}

        {title && (
          <h2
            id="modal-title"
            className="text-dls-h2 font-normal text-primary mb-spacing-4 pr-spacing-8"
          >
            {title}
          </h2>
        )}

        <div className="text-primary">{children}</div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
};
